/**
 * Text styling helpers — Tier 2 Group B.
 *
 * Applies the Group B text-styling fields (letter spacing, line
 * height, stroke with position semantics, drop shadow) onto a
 * Fabric IText. Lives outside `useFabricCanvas` so it can be unit-
 * tested without spinning up the Fabric runtime — the helper takes
 * a structural target plus a factory for `fabric.Shadow` /
 * `fabric.IText` (the clipPath used for inside-stroke positioning).
 *
 * Stroke position semantics:
 *  - `outside` — `paintFirst:'stroke'`. Stroke is drawn before the
 *    fill, so the fill covers the inner half of the stroke band and
 *    only the outer half remains visible. Visible stroke width
 *    equals the user-set `width` (the inner half is hidden by fill).
 *  - `center`  — `paintFirst:'fill'` (Fabric default). Stroke
 *    straddles the glyph edge as canvas2d natively renders.
 *  - `inside`  — `paintFirst:'fill'` plus a self-shaped clipPath
 *    (the IText's own glyph runs). The effective stroke width is
 *    doubled so the half that would normally be outside the glyph
 *    is hidden by the clip, leaving a visible stroke equal to the
 *    user-set `width` on the inside.
 */

import type { TextSettings, TextStrokeSettings, TextShadowSettings } from '../types';

/** Structural shape of a Fabric IText. Avoids importing fabric at
 *  the type level so tests can pass plain objects. */
export interface TextStylingTarget {
  set(props: Record<string, unknown>): unknown;
  get?(key: string): unknown;
  text?: string;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: string | number;
  fontStyle?: string;
}

/** Factory bundle for constructing the fabric.Shadow object. Pure
 *  helpers pass it in so happy-dom tests don't need the fabric
 *  runtime. Inside stroke is handled in-instance by the render
 *  override installed in `useFabricCanvas` (no clipPath needed) so
 *  no factory is required for it here. */
export interface TextStylingDeps {
  /** Build a Fabric Shadow (or compatible) object from the shadow
   *  spec. Return value is assigned to `target.shadow`. */
  createShadow: (spec: TextShadowSettings) => unknown;
}

/** Custom IText property the render override (see
 *  `ensureInsideStrokeRenderOverride` below) reads to decide
 *  whether to render the stroke with `source-atop` compositing.
 *  Lives on the IText instance via `.set()` — Fabric preserves
 *  unknown keys. */
export const INSIDE_STROKE_FLAG = '_fiasInsideStroke';

/** Sentinel property that marks an IText instance as already
 *  patched, so `ensureInsideStrokeRenderOverride` stays idempotent
 *  across the multiple paths that can call it (addText, the
 *  `applyLayerOrder` post-load walk, every state.textSettings
 *  change in `updateActiveText`). */
const INSIDE_STROKE_OVERRIDE_TAG = '__fiasInsideStrokeOverride';

/** Structural shape of a Fabric IText, narrowed to just the
 *  fields the render-override touches. Declared structurally so
 *  the helper can be unit-tested with plain objects (no Fabric
 *  runtime required — happy-dom doesn't support canvas2d).
 *
 *  Method signatures match Fabric's `(ctx: CanvasRenderingContext2D)
 *  => void` so passing an actual `fabric.IText` here is typesafe.
 *  Tests cast their structural fakes to the DOM type via
 *  `as unknown as CanvasRenderingContext2D`. */
export interface InsideStrokeTarget {
  strokeWidth?: number;
  _renderText?: (ctx: CanvasRenderingContext2D) => void;
  _renderTextFill?: (ctx: CanvasRenderingContext2D) => void;
  _renderTextStroke?: (ctx: CanvasRenderingContext2D) => void;
  [INSIDE_STROKE_FLAG]?: boolean;
  [INSIDE_STROKE_OVERRIDE_TAG]?: boolean;
}

/** Install a per-instance `_renderText` override that renders the
 *  Inside-stroke variant via `source-atop` compositing.
 *
 *  Canvas2d natively centers strokes on the path edge — half
 *  inside the glyph silhouette, half outside. Inside stroke needs
 *  the stroke to render entirely WITHIN the silhouette, so the
 *  override re-renders the stroke at 2× width under
 *  `globalCompositeOperation = 'source-atop'` after the fill is
 *  drawn. `source-atop` clips the new content to pixels where the
 *  destination already has alpha — i.e. the filled glyph area —
 *  so the outside half of the doubled stroke band is discarded
 *  and the inside half stays.
 *
 *  Switching the override on / off is driven by `INSIDE_STROKE_FLAG`
 *  on the IText (set via `resolveTextStyleProps`); the override is
 *  installed once per instance and stays installed regardless of
 *  current position so flips between Outside / Center / Inside
 *  don't need to reinstall.
 *
 *  Resilience to Fabric upgrades: if `_renderText` /
 *  `_renderTextFill` / `_renderTextStroke` are renamed or removed
 *  in a future Fabric release, the override gracefully falls back
 *  to the base render (Center behavior) instead of throwing. The
 *  defensive `typeof === 'function'` checks below pin that
 *  fallback. Tests in `text-styling.test.ts` exercise the
 *  fallback path. */
export function ensureInsideStrokeRenderOverride(itext: InsideStrokeTarget): void {
  if (itext[INSIDE_STROKE_OVERRIDE_TAG]) return;
  const baseRenderText = itext._renderText;
  if (typeof baseRenderText !== 'function') return;
  if (typeof itext._renderTextFill !== 'function') return;
  if (typeof itext._renderTextStroke !== 'function') return;

  const bound = baseRenderText.bind(itext);
  itext._renderText = function (ctx: CanvasRenderingContext2D) {
    const self = this as InsideStrokeTarget;
    const insideStroke = !!self[INSIDE_STROKE_FLAG];
    const userStrokeWidth = self.strokeWidth ?? 0;
    if (!insideStroke || userStrokeWidth <= 0) {
      bound(ctx);
      return;
    }
    const renderFill = self._renderTextFill;
    const renderStroke = self._renderTextStroke;
    if (!renderFill || !renderStroke) {
      bound(ctx);
      return;
    }
    renderFill.call(self, ctx);
    ctx.save();
    ctx.globalCompositeOperation = 'source-atop';
    self.strokeWidth = userStrokeWidth * 2;
    try {
      renderStroke.call(self, ctx);
    } finally {
      self.strokeWidth = userStrokeWidth;
      ctx.restore();
    }
  };
  itext[INSIDE_STROKE_OVERRIDE_TAG] = true;
}

/** Resolve a TextSettings into the prop bag that should be applied
 *  to a Fabric IText via `.set()`. Pure — no Fabric required.
 *
 *  Excludes the keys this helper does NOT own:
 *   - `path` (managed by addText / updateActiveText for arch + bind)
 *   - `arch` (round-tripped via `target.archValue`, not a Fabric prop)
 *  The caller is still responsible for those.
 */
export function resolveTextStyleProps(
  settings: TextSettings,
  deps: TextStylingDeps,
): Record<string, unknown> {
  const props: Record<string, unknown> = {
    fontFamily: settings.fontFamily,
    fontSize: settings.fontSize,
    fontWeight: settings.fontWeight,
    fontStyle: settings.fontStyle,
    underline: settings.underline,
    textAlign: settings.textAlign,
    fill: settings.fill,
    charSpacing: settings.letterSpacing,
    lineHeight: settings.lineHeight,
  };

  // Stroke. Absent → clear stroke + reset paintFirst + drop the
  // inside-stroke flag. Present → apply width/color/paintFirst,
  // and toggle the render-override flag for 'inside' position.
  const stroke = settings.stroke;
  if (!stroke || stroke.width <= 0) {
    props.stroke = null;
    props.strokeWidth = 0;
    props.paintFirst = 'fill';
    props[INSIDE_STROKE_FLAG] = false;
  } else {
    applyStrokeProps(props, stroke);
  }

  // Drop shadow. Absent → clear. Present → build a Shadow via the
  // factory. We don't validate the spec here — that's the panel's
  // job; bad numeric values just render funny.
  const shadow = settings.dropShadow;
  if (!shadow) {
    props.shadow = null;
  } else {
    props.shadow = deps.createShadow(shadow);
  }

  return props;
}

function applyStrokeProps(props: Record<string, unknown>, stroke: TextStrokeSettings): void {
  props.stroke = stroke.color;
  props.strokeWidth = stroke.width;
  switch (stroke.position) {
    case 'outside':
      // paintFirst='stroke' draws stroke before fill — fill then
      // covers the inner half of the stroke band so only the
      // outer half remains visible. Stroke band width = user
      // value.
      props.paintFirst = 'stroke';
      props[INSIDE_STROKE_FLAG] = false;
      break;
    case 'center':
      props.paintFirst = 'fill';
      props[INSIDE_STROKE_FLAG] = false;
      break;
    case 'inside':
      // The render override installed in useFabricCanvas reads
      // INSIDE_STROKE_FLAG and renders fill, then stroke at 2× the
      // declared `strokeWidth` under `globalCompositeOperation =
      // 'source-atop'`. That clips the outside half of the stroke
      // band to where the fill already exists, leaving a visible
      // band equal to the user-set width inside the glyph
      // silhouette — true Photoshop "Inside" behavior. We persist
      // the un-doubled width here so the panel + round-trip stays
      // honest; the doubling is a render-time detail.
      props.paintFirst = 'fill';
      props[INSIDE_STROKE_FLAG] = true;
      break;
  }
}
